"use client";

import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Header from "../../components/Header";
import Leftnav from "../../components/Leftnav";
import ProfileCardTournament from "../../components/ProfilecardTournament";
import Load from "../../components/Load";
import PaymentStatusChip from "../../components/PaymentStatusChip";
import InscriptionStatusChip from "../../components/InscriptionStatusChip";
import { useAuth } from "../../context/AuthContext";
import { TorneioProfile } from "../../services/torneioService";
import {
  PagamentoInscricao,
  PAGAMENTO_POR_ID,
  PagamentoPorIdData,
  PagamentoPorIdVars,
} from "../../services/pagamentoInscricaoService";

// Interface auxiliar para o torneio retornado pela query
interface TorneioRaw {
  id: string;
  nome: string;
  dataInicio?: string | null;
  dataFim?: string | null;
  bannerUrl?: string | null;
  avatarUrl?: string | null;
  local?: {
    id: string;
    nome: string;
    endereco?: {
      cidade: { id: string; nome: string; uf: string };
      logradouro: string;
      numero: string;
      complemento?: string | null;
    };
  };
  subtitulo?: string | null;
}

const ViewInscriptionPayment: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
  const atletaId = user?.atleta?.id ?? '';

  const { data, loading, error } = useQuery<PagamentoPorIdData, PagamentoPorIdVars>(
    PAGAMENTO_POR_ID,
    {
      variables: { id: id || "" },
      skip: !id,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  // Calcula dados do pagamento antes dos returns condicionais (regra dos hooks)
  const pagamento = data?.pagamentoInscricaoPorId;
  const inscricoes: PagamentoInscricao["inscricoes"] = pagamento?.inscricoes ?? [];
  const torneioRaw = inscricoes[0]?.categoriaTorneio.torneio as TorneioRaw | undefined;

  // Verifica se o pagamento pertence ao atleta logado (antes dos returns)
  const pagamentoPertenceAoAtleta = atletaId && inscricoes.length > 0
    ? inscricoes.some((inscricao) =>
        inscricao.equipe?.atletas?.some((atleta) => atleta.id === atletaId)
      )
    : false;

  if (loading) return <Load />;
  if (error) {
    return (
      <Fragment>
        <Header />
        <Leftnav />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left pe-0">
              <div className="container mt-5">
                <div className="alert alert-danger" role="alert">
                  Erro ao carregar pagamento.
                </div>
                <button className="btn btn-primary mt-3" onClick={() => navigate("/athlete/payments")}>
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  if (!pagamento || inscricoes.length === 0) {
    return (
      <Fragment>
        <Header />
        <Leftnav />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left pe-0">
              <div className="container mt-5">
                <p>Nenhuma inscrição encontrada.</p>
                <button className="btn btn-primary" onClick={() => navigate("/athlete/payments")}>
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  // Validação de acesso: verifica se o pagamento pertence ao atleta logado
  if (atletaId && !pagamentoPertenceAoAtleta) {
    return (
      <Fragment>
        <Header />
        <Leftnav />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left pe-0">
              <div className="container mt-5">
                <div className="alert alert-warning" role="alert">
                  <strong>Acesso negado:</strong> Este pagamento não pertence à sua conta.
                </div>
                <button className="btn btn-primary mt-3" onClick={() => navigate("/athlete/payments")}>
                  Voltar para Pagamentos
                </button>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  // Transforma o torneioRaw para o formato esperado pelo ProfileCardTournament
  const torneio: TorneioProfile | undefined = torneioRaw
    ? {
        id: torneioRaw.id,
        nome: torneioRaw.nome,
        dataInicio: torneioRaw.dataInicio ?? "",
        dataFim: torneioRaw.dataFim ?? "",
        bannerUrl: torneioRaw.bannerUrl ?? "",
        avatarUrl: torneioRaw.avatarUrl ?? "",
        subtitulo: torneioRaw.subtitulo ?? undefined,
      }
    : undefined;

  const gamesBase = pagamento.valorSemDesconto ?? inscricoes.reduce(
    (sum, c) => sum + (c.categoriaTorneio.precoInscricao ?? 0),
    0
  );
  const desconto = pagamento.descontoAplicado ?? 0;
  const valorFinal = pagamento.valor ?? gamesBase;
  const cupomAplicado = pagamento.cupomAplicado;

  const formatCurrency = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString("pt-BR") : "";

  const handleBack = () => {
    navigate("/athlete/payments");
  };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="row">
              <div className="col-xl-12 col-xxl-12 col-lg-12">
                <ProfileCardTournament torneio={torneio} />
              </div>
            </div>
            <div className="row bg-white rounded-xxl shadow-xs p-4 m-1 mb-4">
              <div className="col-xl-12 col-xxl-12 col-lg-12">
                <div className="row">
                  <div className="col-xl-1">
                    <figure className="avatar w50 me-auto">
                      <img
                        src="/assets/images/user.png"
                        alt="avater"
                        className="p-1 bg-white rounded-xl w-100"
                      />
                    </figure>
                  </div>
                  <div className="col-xl-11">
                    <h5 className="font-xs ls-1 fw-700 text-grey-900">
                      {torneio?.subtitulo || torneio?.nome}
                      <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
                        {torneio?.dataInicio && torneio?.dataFim
                          ? `${formatDate(torneio.dataInicio)} a ${formatDate(torneio.dataFim)}`
                          : torneio?.dataInicio
                          ? formatDate(torneio.dataInicio)
                          : ""}{" "}
                        {torneioRaw?.local?.endereco?.cidade && (
                          <span className="text-primary">
                            {torneioRaw.local.endereco.cidade.nome},{torneioRaw.local.endereco.cidade.uf}
                          </span>
                        )}
                      </span>
                    </h5>
                  </div>
                  <hr />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-lg-6 mb-4">
                  <div className="card border-0 p-4">
                    <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                      Informações do Pagamento
                    </h4>
                    <div className="mb-3">
                      <label className="font-xssss fw-600 text-grey-700">Status</label>
                      <div className="mt-1">
                        <PaymentStatusChip status={pagamento.status} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="font-xssss fw-600 text-grey-700">Valor Total</label>
                      <div className="mt-1">
                        {pagamento.descontoAplicado && pagamento.descontoAplicado > 0 ? (
                          <>
                            <span
                              style={{ textDecoration: "line-through", marginRight: 8 }}
                              className="text-muted"
                            >
                              {formatCurrency(pagamento.valorSemDesconto ?? 0)}
                            </span>
                            <span className="fw-bold text-success">
                              {formatCurrency(pagamento.valor ?? 0)}
                            </span>
                          </>
                        ) : (
                          <span className="fw-bold">{formatCurrency(pagamento.valor ?? 0)}</span>
                        )}
                      </div>
                    </div>
                    {pagamento.meioPagamento && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Meio de Pagamento</label>
                        <div className="mt-1">
                          <span>{pagamento.meioPagamento === "CARTAO" ? "Cartão de Crédito" : "PIX"}</span>
                        </div>
                      </div>
                    )}
                    {pagamento.dataPagamento && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Data de Pagamento</label>
                        <div className="mt-1">
                          <span>{formatDate(pagamento.dataPagamento)}</span>
                        </div>
                      </div>
                    )}
                    {pagamento.dataVencimento && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Data de Vencimento</label>
                        <div className="mt-1">
                          <span>{formatDate(pagamento.dataVencimento)}</span>
                        </div>
                      </div>
                    )}
                    {pagamento.codigoTransacao && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Código de Transação</label>
                        <div className="mt-1">
                          <span className="font-monospace">{pagamento.codigoTransacao}</span>
                        </div>
                      </div>
                    )}
                    {cupomAplicado && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Cupom Aplicado</label>
                        <div className="mt-1">
                          <span className="badge bg-info">
                            {cupomAplicado.codigo} -{" "}
                            {cupomAplicado.tipo === "FIXO"
                              ? formatCurrency(cupomAplicado.valor)
                              : `${cupomAplicado.valor}%`}
                          </span>
                        </div>
                      </div>
                    )}
                    {pagamento.reciboUrl && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Recibo</label>
                        <div className="mt-1">
                          <a
                            href={pagamento.reciboUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            Visualizar Recibo
                          </a>
                        </div>
                      </div>
                    )}
                    {pagamento.pedidosReembolso && pagamento.pedidosReembolso.length > 0 && (
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Reembolso</label>
                        <div className="mt-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => navigate(`/athlete/payments/${pagamento.id}/refunds`)}
                          >
                            Acompanhar Pedido de Reembolso
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 mb-4">
                  <div className="card border-0 p-4 h-100 d-flex flex-column">
                    <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">
                      Detalhes da Inscrição
                    </h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Categoria</th>
                          <th className="text-end">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inscricoes.map((ins) => (
                          <tr key={ins.id}>
                            <td>{ins.categoriaTorneio.nome}</td>
                            <td className="text-end">
                              {formatCurrency(ins.categoriaTorneio.precoInscricao ?? 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th>Subtotal</th>
                          <th className="text-end">{formatCurrency(gamesBase)}</th>
                        </tr>
                        {desconto > 0 && (
                          <tr>
                            <th className="fw-400">Desconto</th>
                            <th className="text-end fw-400">{formatCurrency(-desconto)}</th>
                          </tr>
                        )}
                        <tr>
                          <th>Total</th>
                          <th className="text-end">{formatCurrency(valorFinal)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-xl-12">
                  <h4 className="font-xs ls-1 fw-700 text-grey-900 mb-3">Inscrições</h4>
                  {inscricoes.map((inscricao) => (
                    <div key={inscricao.id} className="card border-0 p-4 mb-3">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="fw-700 text-grey-900">{inscricao.categoriaTorneio.nome}</h5>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                navigate(`/athlete/inscricao/${inscricao.id}`)
                              }
                            >
                              Visualizar Inscrição
                            </button>
                          </div>
                          <div className="mt-2">
                            <label className="font-xssss fw-600 text-grey-700">Status da Inscrição</label>
                            <div className="mt-1">
                              <InscriptionStatusChip status={inscricao.status} />
                            </div>
                          </div>
                          {inscricao.equipe && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Equipe</label>
                              <div className="mt-1">
                                <span className="fw-bold">{inscricao.equipe.nome}</span>
                                {inscricao.equipe.cidade && (
                                  <span className="text-muted ms-2">- {inscricao.equipe.cidade}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {inscricao.dataInscricao && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Data de Inscrição</label>
                              <div className="mt-1">
                                <span>{formatDate(inscricao.dataInscricao)}</span>
                              </div>
                            </div>
                          )}
                          {inscricao.observacao && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Observações</label>
                              <div className="mt-1">
                                <span>{inscricao.observacao}</span>
                              </div>
                            </div>
                          )}
                          {inscricao.dataCancelamento && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Data de Cancelamento</label>
                              <div className="mt-1">
                                <span>{formatDate(inscricao.dataCancelamento)}</span>
                              </div>
                            </div>
                          )}
                          {inscricao.motivoCancelamento && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Motivo do Cancelamento</label>
                              <div className="mt-1">
                                <span className="text-danger">{inscricao.motivoCancelamento}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          {inscricao.equipe?.atletas && inscricao.equipe.atletas.length > 0 && (
                            <div className="mt-2">
                              <label className="font-xssss fw-600 text-grey-700">Atletas da Equipe</label>
                              <div className="mt-2">
                                {inscricao.equipe.atletas.map((atleta) => (
                                  <div key={atleta.id} className="d-flex align-items-center mb-2">
                                    {atleta.usuario?.imagemUrl ? (
                                      <img
                                        src={atleta.usuario.imagemUrl}
                                        alt={atleta.usuario?.nome ?? 'Atleta'}
                                        className="rounded-circle me-2"
                                        style={{ width: "32px", height: "32px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle me-2 d-flex align-items-center justify-content-center bg-secondary text-white"
                                        style={{ width: "32px", height: "32px", fontSize: "12px" }}
                                      >
                                        {(atleta.usuario?.nome ?? 'Atleta').charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div>
                                      <div className="fw-600">{atleta.usuario?.nome ?? 'Atleta'}</div>
                                      {atleta.usuario?.email && (
                                        <div className="font-xssss text-muted">{atleta.usuario.email}</div>
                                      )}
                                      {atleta.telefone && (
                                        <div className="font-xssss text-muted">Tel: {atleta.telefone}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-flex justify-content-start mt-4">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleBack}
                >
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ViewInscriptionPayment;

